/**
 * BankID Mock - LOCALHOST ONLY. Test error states without real Swedish BankID.
 *
 * Activate with: ?bankid_mock=<scenario> (only on http://localhost)
 * Scenarios: success | init_failed | verify_failed | expired | cancelled | unauthorized
 *
 * Flow: Click BankID button → (mock) redirects back with ?grandidsession=mock_<scenario>
 *       → verify runs with mock sessionId → returns success or throws configured error
 *
 * Does NOT run on staging or production.
 */

import { isLocalhost } from "./utils";
import {
  BankIdSessionResponse,
  InitiateBankIdResponse,
} from "@/store/slices/bankid.slice";
import { ERROR_CODES } from "./error-messages";

export type BankIdMockScenario =
  | "success"
  | "init_failed"
  | "verify_failed"
  | "expired"
  | "cancelled"
  | "unauthorized";

const MOCK_SESSION_PREFIX = "mock_";

export function getBankIdMockScenario(): BankIdMockScenario | null {
  if (!isLocalhost()) return null;
  const params = new URLSearchParams(window.location.search);
  const scenario = params.get("bankid_mock") as BankIdMockScenario | null;
  const valid: BankIdMockScenario[] = [
    "success",
    "init_failed",
    "verify_failed",
    "expired",
    "cancelled",
    "unauthorized",
  ];
  return scenario && valid.includes(scenario) ? scenario : null;
}

export function isMockSessionId(sessionId: string): boolean {
  return sessionId.startsWith(MOCK_SESSION_PREFIX);
}

export function extractMockScenario(sessionId: string): BankIdMockScenario | null {
  if (!isMockSessionId(sessionId)) return null;
  const scenario = sessionId.replace(MOCK_SESSION_PREFIX, "") as BankIdMockScenario;
  const valid: BankIdMockScenario[] = [
    "success",
    "verify_failed",
    "expired",
    "cancelled",
    "unauthorized",
  ];
  return valid.includes(scenario) ? scenario : "verify_failed";
}

const MOCK_VERIFICATION_RESPONSE: BankIdSessionResponse = {
  sessionId: "mock_success",
  username: "test",
  userAttributes: {
    name: "Adam Hassan",
    givenName: "Adam",
    surname: "Hassan",
    personalNumber: "199001011234",
    SPARv2: {
      ns4PersonId: {
        ns4IdNummer: "199001011234",
        ns4Typ: "PERSONNUMMER",
      },
      ns5Sekretessmarkering: "NEJ",
      ns5SkyddadFolkbokforing: "NEJ",
      ns4SenasteAndringSPAR: "",
      ns8Namn: {
        ns2DatumFrom: "2020-01-01",
        ns2DatumTill: "2030-12-31",
        ns7Fornamn: "Adam",
        ns7Tilltalsnamn: "Adam",
        ns7Efternamn: "Hassan",
      },
      ns10Persondetaljer: {
        ns2DatumFrom: "2020-01-01",
        ns2DatumTill: "2030-12-31",
        ns5Sekretessmarkering: "NEJ",
        ns5SkyddadFolkbokforing: "NEJ",
        ns10Fodelsedatum: "1990-01-01",
        ns10Kon: "MAN",
      },
      ns15Folkbokforingsadress: {
        ns3SvenskAdress: {
          ns2DatumFrom: "2020-01-01",
          ns2DatumTill: "2030-12-31",
          ns3Utdelningsadress2: "Björkrisvägen 10 S",
          ns3PostNr: "70234",
          ns3Postort: "Örebro",
        },
      },
    },
  },
};

const SCENARIO_TO_ERROR_CODE: Record<string, string> = {
  verify_failed: ERROR_CODES.FAILED_TO_VERIFY_BANKID,
  expired: ERROR_CODES.BANKID_SESSION_EXPIRED,
  cancelled: ERROR_CODES.BANKID_USER_CANCELLED,
  unauthorized: ERROR_CODES.BANKID_UNAUTHORIZED,
};

export function mockInitiateBankId(
  _data: unknown,
  callbackUrl: string
): InitiateBankIdResponse | null {
  const scenario = getBankIdMockScenario();
  if (!scenario || scenario === "init_failed") return null;

  // Simulate redirect back with mock session
  const url = new URL(callbackUrl);
  url.searchParams.set("grandidsession", `${MOCK_SESSION_PREFIX}${scenario}`);
  url.searchParams.set("bankid_mock", scenario);
  window.location.href = url.toString();
  return { sessionId: `mock_${scenario}`, redirectUrl: url.toString() };
}

export function mockVerifyBankIdSession(
  sessionId: string
): BankIdSessionResponse | { errorCode: string } {
  const scenario = extractMockScenario(sessionId);
  if (!scenario) return { errorCode: ERROR_CODES.FAILED_TO_VERIFY_BANKID };

  if (scenario === "success") {
    return MOCK_VERIFICATION_RESPONSE;
  }

  const errorCode = SCENARIO_TO_ERROR_CODE[scenario] ?? ERROR_CODES.FAILED_TO_VERIFY_BANKID;
  return { errorCode };
}
